import { Users, Coins, TrendingUp, Gift, Settings as SettingsIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { ReferralBoostPanel } from './ReferralBoostPanel';
import { PendingReferralsPanel } from './PendingReferralsPanel';

export const ReferralsAdminTab = ({
    referralStats,
    settings,
    handleSettingsUpdate,
}) => (
    <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="card-dark p-5 text-center">
                <Users className="w-7 h-7 text-[#00FFFF] mx-auto mb-2" />
                <p className="text-2xl font-mono font-bold text-white">{referralStats?.total_referrals || 0}</p>
                <p className="text-[#A0AAB2] text-xs">Total Referrals</p>
            </div>
            <div className="card-dark p-5 text-center">
                <Coins className="w-7 h-7 text-[#FFD700] mx-auto mb-2" />
                <p className="text-2xl font-mono font-bold text-[#FFD700]">{(referralStats?.total_bonus_paid || 0).toFixed(2)}</p>
                <p className="text-[#A0AAB2] text-xs">Total Bonuses Paid</p>
            </div>
            <div className="card-dark p-5 text-center">
                <TrendingUp className="w-7 h-7 text-[#39FF14] mx-auto mb-2" />
                <p className="text-2xl font-mono font-bold text-[#39FF14]">{(referralStats?.total_referrer_bonus || 0).toFixed(2)}</p>
                <p className="text-[#A0AAB2] text-xs">Referrer Bonuses</p>
            </div>
            <div className="card-dark p-5 text-center">
                <Gift className="w-7 h-7 text-[#FF007F] mx-auto mb-2" />
                <p className="text-2xl font-mono font-bold text-[#FF007F]">{(referralStats?.total_referee_bonus || 0).toFixed(2)}</p>
                <p className="text-[#A0AAB2] text-xs">New User Bonuses</p>
            </div>
        </div>

        <div className="card-dark p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <SettingsIcon className="w-5 h-5 text-[#00FFFF]" />
                Referral Settings
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-center justify-between">
                    <div>
                        <Label className="text-white">Referrals Enabled</Label>
                        <p className="text-xs text-[#A0AAB2]">Allow referral signups</p>
                    </div>
                    <Switch
                        checked={settings.referral?.enabled !== false}
                        onCheckedChange={(v) => handleSettingsUpdate('referral', 'enabled', v)}
                        data-testid="referral-enabled-toggle"
                    />
                </div>
                <div>
                    <Label className="text-white">Max Referrals Per User (0 = unlimited)</Label>
                    <Input
                        type="number"
                        value={settings.referral?.max_referrals_per_user ?? 0}
                        onChange={(e) => handleSettingsUpdate('referral', 'max_referrals_per_user', parseInt(e.target.value) || 0)}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="referral-max-per-user"
                    />
                </div>
                <div>
                    <Label className="text-white">Referrer Bonus (credits)</Label>
                    <Input
                        type="number"
                        value={settings.referral?.referrer_bonus ?? 10}
                        onChange={(e) => handleSettingsUpdate('referral', 'referrer_bonus', parseFloat(e.target.value) || 0)}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="referral-referrer-bonus"
                    />
                </div>
                <div>
                    <Label className="text-white">New Player Bonus (credits)</Label>
                    <Input
                        type="number"
                        value={settings.referral?.referee_bonus ?? 0}
                        onChange={(e) => handleSettingsUpdate('referral', 'referee_bonus', parseFloat(e.target.value) || 0)}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="referral-referee-bonus"
                    />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-white">Minimum Load to Trigger Payout ($)</Label>
                    <p className="text-xs text-[#A0AAB2] mt-1">
                        Anti-fraud guard. Bonuses are <span className="text-[#FFD700]">held in escrow</span> until the new player has cumulatively loaded at least this much. Set to <span className="font-mono">0</span> to pay bonuses immediately on signup (legacy behavior).
                    </p>
                    <Input
                        type="number"
                        min="0"
                        step="1"
                        value={settings.referral?.load_threshold_for_payout ?? 20}
                        onChange={(e) => handleSettingsUpdate('referral', 'load_threshold_for_payout', parseFloat(e.target.value) || 0)}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white max-w-xs"
                        data-testid="referral-load-threshold"
                    />
                </div>
            </div>
        </div>

        <ReferralBoostPanel
            baseReferrer={settings.referral?.referrer_bonus ?? 10}
            baseReferee={settings.referral?.referee_bonus ?? 0}
        />

        <PendingReferralsPanel />

        {(referralStats?.top_referrers || []).length > 0 && (
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4">Top Referrers</h3>
                <Table>
                    <TableHeader>
                        <TableRow className="border-white/10">
                            <TableHead className="text-[#A0AAB2]">#</TableHead>
                            <TableHead className="text-[#A0AAB2]">Name</TableHead>
                            <TableHead className="text-[#A0AAB2]">Email</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Referrals</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Earned</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {referralStats.top_referrers.map((ref, i) => (
                            <TableRow key={i} className="border-white/10">
                                <TableCell className={i < 3 ? 'text-[#FFD700] font-bold' : 'text-[#A0AAB2]'}>{i + 1}</TableCell>
                                <TableCell className="text-white font-medium">{ref.name}</TableCell>
                                <TableCell className="text-[#A0AAB2]">{ref.email}</TableCell>
                                <TableCell className="text-right font-mono text-white">{ref.referral_count}</TableCell>
                                <TableCell className="text-right font-mono text-[#FFD700]">{(ref.referral_earnings || 0).toFixed(2)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        )}

        {(referralStats?.recent_referrals || []).length > 0 && (
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4">Recent Referrals</h3>
                <div className="space-y-2">
                    {referralStats.recent_referrals.map((ref, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                            <div className="flex items-center gap-3">
                                <span className="text-white font-medium">{ref.referrer_name}</span>
                                <span className="text-[#A0AAB2]">invited</span>
                                <span className="text-[#39FF14] font-medium">{ref.referee_name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                                <span className="font-mono text-[#FFD700]">+{ref.referrer_bonus} / +{ref.referee_bonus}</span>
                                <span className="text-[#A0AAB2]">{ref.created_at ? new Date(ref.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : ''}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);
